<template>
  <div>
    <h4 class="tw-text-lg tw-font-semibold tw-mb-4">{{ t('users.actions.edit') }}</h4>

    <!-- User Data Card (read-only except admin toggle) -->
    <Card class="tw-mb-6">
      <CardHeader>
        <CardTitle class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
          {{ t('users.titles.user_data') }}
        </CardTitle>
      </CardHeader>
      <CardContent class="tw-p-6">
        <div class="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6">
          <!-- First Name -->
          <div class="tw-space-y-1">
            <Label class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block">
              {{ t('users.titles.first_name') }}
            </Label>
            <Input
              :model-value="currentUser?.first_name || ''"
              disabled
              :placeholder="t('users.data.no_first_name')"
              class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 disabled:tw-opacity-60"
            />
          </div>

          <!-- Last Name -->
          <div class="tw-space-y-1">
            <Label class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block">
              {{ t('users.titles.last_name') }}
            </Label>
            <Input
              :model-value="currentUser?.last_name || ''"
              disabled
              :placeholder="t('users.data.no_last_name')"
              class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 disabled:tw-opacity-60"
            />
          </div>

          <!-- Email -->
          <div class="tw-space-y-1">
            <Label class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block">
              {{ t('common.titles.email') }}
            </Label>
            <Input
              :model-value="currentUser?.email || ''"
              disabled
              type="email"
              :placeholder="t('users.data.no_email')"
              class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 disabled:tw-opacity-60"
            />
          </div>

          <!-- Is Admin -->
          <div class="tw-flex tw-items-end tw-pb-2">
            <div class="tw-flex tw-items-center tw-gap-3">
              <Switch
                id="isAdmin"
                :model-value="currentUser?.is_admin || false"
                @update:model-value="toggleIsAdmin"
              />
              <Label for="isAdmin" class="tw-text-sm tw-font-medium tw-text-secondary-700">
                {{ t('users.titles.is_admin') }}
              </Label>
            </div>
          </div>
        </div>

        <!-- Admin Confirmation Dialog -->
        <Dialog
          :open="showAdminConfirmDialog"
          @update:open="(val: boolean) => { if (!val && !isTogglingAdmin) cancelAdminToggle() }"
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{{ t('users.titles.confirm_admin_assign_title') }}</DialogTitle>
              <DialogDescription>
                {{ t('users.titles.confirm_admin_assign_description') }}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" :disabled="isTogglingAdmin" @click="cancelAdminToggle">
                {{ t('common.forms.cancel') }}
              </Button>
              <Button :disabled="isTogglingAdmin" @click="confirmAdminToggle">
                <Loader v-if="isTogglingAdmin" class="tw-h-4 tw-w-4 tw-animate-spin" />
                {{ t('common.forms.confirm') }}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>

    <!-- Customer Assignments Card -->
    <Card>
      <CardHeader>
        <CardTitle class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
          Customer Assignments
        </CardTitle>
      </CardHeader>
      <CardContent class="tw-p-6">
        <div
          v-if="!assignmentRows.length"
          class="tw-p-4 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-md tw-text-yellow-800 tw-text-sm"
        >
          No roles associated with this user.
        </div>

        <div v-else class="tw-space-y-4">
          <Collapsible
            v-for="(row, index) in assignmentRows"
            :key="row.customerId"
            :default-open="true"
            v-slot="{ open }"
          >
            <Card class="tw-border tw-border-secondary-200 tw-overflow-hidden">
              <CollapsibleTrigger as-child>
                <div
                  class="tw-flex tw-items-center tw-justify-between tw-py-3 tw-px-4 tw-bg-secondary-50 tw-cursor-pointer hover:tw-bg-secondary-100 tw-transition-colors"
                >
                  <span class="tw-text-sm tw-font-semibold tw-text-secondary-700">
                    {{ row.customerName }}
                  </span>
                  <ChevronDown
                    class="tw-w-4 tw-h-4 tw-text-secondary-500 tw-shrink-0 tw-transition-transform tw-duration-200"
                    :class="{ 'tw-rotate-180': open }"
                  />
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent class="tw-p-4" :style="{ paddingTop: '1.25rem' }">
                  <div class="tw-grid tw-grid-cols-1 tw-gap-4">
                    <!-- Roles -->
                    <div class="tw-space-y-1">
                      <Label
                        class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
                      >
                        {{ t('users.titles.role') }}
                      </Label>
                      <Select
                        multiple
                        :model-value="row.roles"
                        @update:model-value="row.roles = $event as string[]"
                      >
                        <SelectTrigger
                          class="tw-w-full tw-min-h-[48px] tw-h-auto tw-px-3 tw-py-2 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                        >
                          <SelectValue :placeholder="t('users.titles.select_roles')" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            v-for="(label, key) in selectableRolesOptions"
                            :key="key"
                            :value="key"
                          >
                            {{ label }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <!-- Clearing Groups -->
                    <div class="tw-grid tw-grid-cols-2 tw-gap-x-8">
                      <!-- Order Clearing Group -->
                      <div class="tw-space-y-1">
                        <Label
                          class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
                        >
                          {{ t('users.titles.order_clearing_group') }}
                        </Label>
                        <Select
                          :model-value="
                            row.selectedOrderGroupId != null
                              ? String(row.selectedOrderGroupId)
                              : 'none'
                          "
                          @update:model-value="
                            val => (row.selectedOrderGroupId = val === 'none' ? null : Number(val))
                          "
                        >
                          <SelectTrigger
                            class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                          >
                            <SelectValue :placeholder="t('common.titles.none')" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{{ t('common.titles.none') }}</SelectItem>
                            <SelectItem
                              v-for="group in row.orderGroups"
                              :key="group.id"
                              :value="String(group.id)"
                            >
                              {{ group.name }}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <!-- Payment Clearing Group -->
                      <div class="tw-space-y-1">
                        <Label
                          class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
                        >
                          {{ t('users.titles.payment_clearing_group') }}
                        </Label>
                        <Select
                          :model-value="
                            row.selectedPaymentGroupId != null
                              ? String(row.selectedPaymentGroupId)
                              : 'none'
                          "
                          @update:model-value="
                            val =>
                              (row.selectedPaymentGroupId = val === 'none' ? null : Number(val))
                          "
                        >
                          <SelectTrigger
                            class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                          >
                            <SelectValue :placeholder="t('common.titles.none')" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{{ t('common.titles.none') }}</SelectItem>
                            <SelectItem
                              v-for="group in row.paymentGroups"
                              :key="group.id"
                              :value="String(group.id)"
                            >
                              {{ group.name }}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>

                <!-- Actions -->
                <div class="tw-flex tw-gap-2 tw-justify-end tw-px-4 tw-pb-4">
                  <Button
                    size="sm"
                    class="tw-gap-2 !tw-bg-secondary-500 tw-text-white"
                    @click="saveUserRoles(index)"
                  >
                    <Save class="tw-w-4 tw-h-4" />
                    {{ t('common.forms.save') }}
                  </Button>
                  <Dialog
                    :open="deleteConfirmIndex === index"
                    @update:open="(val: boolean) => { if (!val && !isDeleting) deleteConfirmIndex = null }"
                  >
                    <DialogTrigger as-child>
                      <Button variant="destructive" size="sm" class="tw-gap-2">
                        <Trash2 class="tw-w-4 tw-h-4" />
                        {{ t('common.forms.delete') }}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {{ t('common.forms.delete') }} — {{ row.customerName }}
                        </DialogTitle>
                        <DialogDescription>
                          This will remove all roles and clearing group assignments for this
                          customer. This action cannot be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          :disabled="isDeleting"
                          @click="deleteConfirmIndex = null"
                        >
                          {{ t('common.forms.cancel') }}
                        </Button>
                        <Button
                          variant="destructive"
                          :disabled="isDeleting"
                          @click="deleteAssignment(index)"
                        >
                          <Loader v-if="isDeleting" class="tw-h-4 tw-w-4 tw-animate-spin" />
                          {{ t('common.forms.delete') }}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { ChevronDown, Loader, Save, Trash2 } from 'lucide-vue-next';
import { onMounted, reactive, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';

import { UserRoles, type User } from '@/_helpers/interfaces';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useCustomerStore } from '@/stores/customer.store';
import { useUserStore } from '@/stores/user.store';

interface ClearingGroup {
  id: number;
  name: string;
  display_order: number;
  assignees: number[];
}

interface CustomerAssignmentRow {
  customerId: number;
  customerName: string;
  roles: string[];
  orderGroups: ClearingGroup[];
  paymentGroups: ClearingGroup[];
  selectedOrderGroupId: number | null;
  selectedPaymentGroupId: number | null;
}

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const toast = useToast();
const router = useRouter();
const customerStore = useCustomerStore();
const { getUserById, getAssignedCustomersWithRolesList, editUserAdminStatus, editUserAssignments } =
  useUserStore();

// Selectable roles: exclude ADMIN, ORDER_CLEARER, PAYMENT_CLEARER
const CLEARER_ROLES = ['ORDER_CLEARER', 'PAYMENT_CLEARER'];
const selectableRolesOptions: Record<string, string> = {};
for (const [key, label] of Object.entries(UserRoles)) {
  if (!['ADMIN', 'ORDER_CLEARER', 'PAYMENT_CLEARER'].includes(key)) {
    selectableRolesOptions[key] = label;
  }
}

const props = defineProps({
  userId: String,
});

const currentUser: Ref<User | null> = ref(null);
const assignmentRows = reactive<CustomerAssignmentRow[]>([]);
const deleteConfirmIndex = ref<number | null>(null);
const showAdminConfirmDialog = ref(false);
const isDeleting = ref(false);
const isTogglingAdmin = ref(false);

const findUserGroupId = (groups: ClearingGroup[], userId: number): number | null => {
  for (const group of groups) {
    if (group.assignees.includes(userId)) {
      return group.id;
    }
  }
  return null;
};

onMounted(async () => {
  if (!props.userId || isNaN(+props.userId)) {
    router.push({ name: 'list-users' });
    return;
  }

  currentUser.value = await getUserById(+props.userId);
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.users',
      to: { name: 'list-users' },
    },
    {
      label: currentUser.value
        ? `${currentUser.value.first_name} ${currentUser.value.last_name}`
        : '',
    },
    {
      labelKey: 'common.forms.edit',
    },
  ]);

  const customersWithRoles = getAssignedCustomersWithRolesList(currentUser.value);

  // Build assignment rows with clearing group data
  for (const cwr of customersWithRoles) {
    let orderGroups: ClearingGroup[] = [];
    let paymentGroups: ClearingGroup[] = [];

    try {
      const clearingData = await customerStore.getClearingGroups(cwr.customerId);
      orderGroups = clearingData.order_groups || [];
      paymentGroups = clearingData.payment_groups || [];
    } catch {
      // Clearing groups not available for this customer
    }

    const selectableRolesForCustomer = cwr.roles.filter(
      (role: string) => !CLEARER_ROLES.includes(role)
    );

    assignmentRows.push({
      customerId: cwr.customerId,
      customerName: cwr.customerName,
      roles: selectableRolesForCustomer,
      orderGroups,
      paymentGroups,
      selectedOrderGroupId: findUserGroupId(orderGroups, currentUser.value!.id),
      selectedPaymentGroupId: findUserGroupId(paymentGroups, currentUser.value!.id),
    });
  }
});

const saveUserRoles = async (index: number) => {
  const row = assignmentRows[index];
  try {
    await editUserAssignments(+props.userId!, {
      customer: row.customerId,
      roles: row.roles,
      order_clearing_group: row.selectedOrderGroupId,
      payment_clearing_group: row.selectedPaymentGroupId,
    });

    toast.open({
      message: t('users.titles.successful_update'),
      type: 'success',
    });
  } catch {
    toast.open({
      message: t('common.errors.something_went_wrong'),
      type: 'error',
    });
  }
};

const deleteAssignment = async (index: number) => {
  const row = assignmentRows[index];
  isDeleting.value = true;
  try {
    await editUserAssignments(+props.userId!, {
      customer: row.customerId,
      roles: [],
      order_clearing_group: null,
      payment_clearing_group: null,
    });

    deleteConfirmIndex.value = null;
    assignmentRows.splice(index, 1);

    toast.open({
      message: t('users.titles.successful_delete'),
      type: 'success',
    });
  } catch {
    toast.open({
      message: t('common.errors.something_went_wrong'),
      type: 'error',
    });
  } finally {
    isDeleting.value = false;
  }
};

const toggleIsAdmin = (isAdmin: boolean) => {
  if (isAdmin && assignmentRows.length > 0) {
    showAdminConfirmDialog.value = true;
    return;
  }
  performToggleAdmin(isAdmin);
};

const performToggleAdmin = async (isAdmin: boolean) => {
  try {
    await editUserAdminStatus(+props.userId!, isAdmin);
    if (currentUser.value) {
      currentUser.value.is_admin = isAdmin;
    }

    if (isAdmin) {
      assignmentRows.splice(0, assignmentRows.length);
    }

    const message = isAdmin
      ? t('users.titles.successful_admin_assign')
      : t('users.titles.successful_admin_remove');

    toast.open({
      message,
      type: 'success',
    });
  } catch {
    toast.open({
      message: t('common.errors.something_went_wrong'),
      type: 'error',
    });
  }
};

const confirmAdminToggle = async () => {
  isTogglingAdmin.value = true;
  try {
    await performToggleAdmin(true);
  } finally {
    isTogglingAdmin.value = false;
    showAdminConfirmDialog.value = false;
  }
};

const cancelAdminToggle = () => {
  showAdminConfirmDialog.value = false;
};
</script>
